import { IConfigSeller } from '@/models/configSeller'
import { IAppStore } from '@/models/store.model'
import { webApiService } from '@/services'
import { canEvaluateBatteryWithSwitch } from '@/utils/canEvaluateBatteryWithSwitch'
import { canShowIAEvaluation, hasPendingFeasibility, hasPendingRecalculate } from '@/utils/canShowIAEvaluation'
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Spinner, useDisclosure } from '@nextui-org/react'
import { enqueueSnackbar } from 'notistack'
import qrCode from 'qrcode'
import { FC, useEffect, useState } from 'react'
import { MdCheckCircle, MdError, MdQrCode2 } from 'react-icons/md'
import { useSelector } from 'react-redux'
import CheckFeasibility from '../../../TradeInPage/components/CheckFeasibility'
import Discounts from '../../../TradeInPage/components/Discounts'
import IAEvaluation from '../../../TradeInPage/components/IAEvaluation'
import Recalculate from '../../../TradeInPage/components/Recalculate'
import SwitchBattery from '../../../TradeInPage/components/SwitchBattery'
import { JoyRideProvider } from '../../../TradeInPage/context/JoyRideContext'
import { TradeinProvider } from '../../../TradeInPage/context/TradeinContext'
import { useBatteryModelSupport } from '../../../TradeInPage/hooks/useBatteryModelSupport'
import { useTradeinContext } from '../../../TradeInPage/hooks/useTradeinContext'

/* Estado 4 = Validado: a partir de ahí el modal deja de ser la prueba de Phonecheck
   y pasa a ser el flujo de evaluación estética -> batería -> recibir. */
const STATUS_VALIDADO = 4
/* Desde Entregado el equipo ya se recibió. Los cancelados quedan fuera de esta lista
   porque todavía admiten recálculo o revisión de factibilidad. */
const ESTADOS_FINALIZADOS = ['ENTREGADO', 'CUSTODIA', 'EN BODEGA', 'RETIRADO REUSE']

interface IOwnProps {
  item: any
  onDone: () => void
}

interface IContentProps {
  loginCode: string
  onClose: () => void
  onDone: () => void
}

/* ------------------------- Recalcular / Revisar factibilidad -------------------------
   Mismas condiciones con las que se muestran estos botones en el header del detalle. */
const AccionesTradein: FC = () => {
  const { ctx } = useTradeinContext()
  const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)
  const userState = useSelector((store: IAppStore) => store.user)

  const isSoloLectura = [Number(import.meta.env.VITE_PERFIL_SOLOLECTURA)].includes(userState.id_role)
  const isHuaweiOnlineChile = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_cl'

  const showRecalculate = ctx.tradein.recalculate === '1' &&
    ctx.tradein.wasRecalculated === null &&
    ctx.tradein.id_status !== 8 &&
    !isSoloLectura

  const showFeasibility = (
    (ctx.tradein.certificate_theft === 'VALID' && ctx.tradein.cancel_reason === 'El modelo evaluado no esta disponible' && ctx.tradein.grade_IA === null && ctx.tradein.id_status === 8) ||
    (ctx.tradein.certificate_theft === 'VALID' && ctx.tradein.cancel_reason === 'El modelo evaluado no esta disponible' && ctx.tradein.grade_IA === null && ctx.tradein.id_status === 11 && isHuaweiOnlineChile)
  ) && (ctx.tradein.sbu === 'Smartphone' || ctx.tradein.sbu === 'Tablet') && !isSoloLectura

  if (!showRecalculate && !showFeasibility) return null

  return (
    <div className='flex flex-wrap items-center gap-2 mr-auto'>
      {showRecalculate && <Recalculate />}
      {showFeasibility && <CheckFeasibility />}
    </div>
  )
}

/* ----------------------------- Prueba de Phonecheck ----------------------------- */
const TestPhonecheck: FC<IContentProps> = ({ loginCode, onClose, onDone }) => {
  const { ctx } = useTradeinContext()
  const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)
  const [urlQrCode, setUrlQrCode] = useState<string | null>(null)
  const [isCheckingCode, setIsCheckingCode] = useState(false)

  useEffect(() => {
    if (ctx.tradein.id === 0) return
    // Primero se intenta el QR que genera el back en S3, si no existe se arma en el navegador.
    setUrlQrCode(`https://${import.meta.env.VITE_BUCKET}.s3.us-west-2.amazonaws.com/${configSeller.config.s3.FOLDER_APP}/imagenes/${ctx.tradein.id}/login_code_qr.png`)
  }, [ctx.tradein.id, configSeller.config.s3.FOLDER_APP])

  const handleQrError = () => {
    if (!loginCode) return
    qrCode.toDataURL(`https://cloudportal.phonecheck.com/cloud/remoteApp/configuration?pin=${loginCode}`, { errorCorrectionLevel: 'H' }, (err, url) => {
      if (!err) setUrlQrCode(url)
    })
  }

  const checkLoginCode = async () => {
    setIsCheckingCode(true)
    try {
      const res = await webApiService.checkLoginCode({ idTradein: ctx.tradein.id, loginCode })
      if (res.success) {
        enqueueSnackbar(res.message, { variant: 'success' })
        onDone()
        onClose()
      } else {
        enqueueSnackbar(res.message, { variant: 'info' })
      }
    } catch (error) {
      console.log('🚀 ~ checkLoginCode ~ error:', error)
      enqueueSnackbar('Error al verificar el código. Por favor, vuelva a intentar.', { variant: 'error' })
    } finally {
      setIsCheckingCode(false)
    }
  }

  return (
    <>
      <ModalHeader className='flex flex-col gap-1'>
        Realizar prueba
        <span className='text-sm font-normal text-slate-400'>
          El equipo debe correr el test desde la aplicación Phonecheck antes de verificar el código.
        </span>
      </ModalHeader>
      <ModalBody>
        {/* Código de evaluación */}
        <div className='rounded-xl bg-slate-50 p-3 text-center'>
          <p className='text-xs font-semibold text-slate-500'>Código de evaluación</p>
          <p className='font-black text-3xl tracking-[0.3em] text-primary-600 mt-1'>{loginCode}</p>
        </div>

        {/* QR del mismo código */}
        <div className='flex flex-col items-center gap-2 rounded-xl border-1 border-slate-200 px-4 py-5'>
          <div className='flex items-center gap-2 text-slate-500'>
            <MdQrCode2 className='text-xl shrink-0' />
            <span className='text-sm font-medium'>Ingresa el código o escanea el QR</span>
          </div>
          {urlQrCode !== null
            ? <img src={urlQrCode} alt='Código QR de la prueba' onError={handleQrError} width={190} height={190} className='rounded-lg' />
            : <Spinner size='sm' />}
          <span className='text-xs text-slate-400'>Aplicación Phonecheck</span>
        </div>
      </ModalBody>
      <ModalFooter>
        <AccionesTradein />
        <Button variant='light' onPress={onClose} isDisabled={isCheckingCode}>Cancelar</Button>
        <Button color='primary' onPress={checkLoginCode} isLoading={isCheckingCode} startContent={!isCheckingCode ? <MdCheckCircle className='text-base' /> : null}>
          {isCheckingCode ? 'Verificando…' : 'Verificar Código'}
        </Button>
      </ModalFooter>
    </>
  )
}

/* --------------------- Evaluación estética -> batería -> recibir --------------------- */
const AestheticEvaluation: FC<IContentProps> = ({ onClose, onDone }) => {
  const { ctx } = useTradeinContext()
  const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)
  const userState = useSelector((store: IAppStore) => store.user)
  const [loadingRecibir, setLoadingRecibir] = useState(false)

  const hasGradeIA = ctx.tradein.grade_IA !== null
  const hasBatteryStatus = ctx.tradein.isBatteryHealthy !== null
  const isMacstoreMexico = configSeller.config.s3.FOLDER_APP === 'ti_macstore'
  const { isBatteryModelSupported } = useBatteryModelSupport(
    ctx.tradein.brand,
    ctx.tradein.model !== '' ? ctx.tradein.model : ctx.tradein.modelName
  )
  // Misma regla del detalle: Discounts para el flujo con % de salud, SwitchBattery para el simple,
  // salvo Macstore México, que los modelos del catálogo de batería también los evalúa con Discounts.
  const useDiscounts = canEvaluateBatteryWithSwitch(configSeller, ctx.tradein.id_sbu, ctx.tradein.brand) ||
    (isMacstoreMexico && isBatteryModelSupported)
  // Con un recálculo o una factibilidad pendiente no corresponde evaluar todavía.
  const showIAEvaluation = canShowIAEvaluation(configSeller, ctx.tradein)
  const pendingRecalculate = hasPendingRecalculate(ctx.tradein)
  const pendingFeasibility = hasPendingFeasibility(ctx.tradein)
  // La batería se evalúa siempre en este flujo, sin importar ENABLE_CHECK_BATTERY del seller.
  const showBattery = showIAEvaluation && hasGradeIA
  const canRecibir = showIAEvaluation && hasBatteryStatus

  // Mismo update que el handleRecibir del detalle del tradein.
  const handleRecibir = async () => {
    setLoadingRecibir(true)
    try {
      await webApiService.update({
        id_tradein: String(ctx.tradein.id),
        id_user: userState.id,
        stage: 6,
        id_role: userState.id_role,
        email: userState.email,
        client_email: ctx.tradein.email,
        id_seller: ctx.tradein.id_seller,
        rut: ctx.tradein.rut,
        client_name: ctx.tradein.name,
        client_lastname: ctx.tradein.lastname,
        url_contract: ctx.tradein.url_contract,
        recipient_name: ctx.tradein.recipient_name,
        recipient_lastname: ctx.tradein.recipient_lastname,
        recipient_rut: ctx.tradein.recipient_rut || '',
        recipient_id: ctx.tradein.recipient_id || '',
        device_purchased: ctx.tradein.device_purchased
      })
      enqueueSnackbar('Estado actualizado a Entregado', { variant: 'success' })
      onDone()
      onClose()
    } catch (error: any) {
      console.log('🚀 ~ handleRecibir ~ error:', error)
      enqueueSnackbar(error?.response?.data?.message ?? 'Error al recibir el equipo', { variant: 'error' })
    } finally {
      setLoadingRecibir(false)
    }
  }

  return (
    <>
      {/* El título dice "Evaluaciones" porque cada componente ya trae el suyo propio. */}
      <ModalHeader className='flex flex-col gap-1'>
        Evaluaciones
        <span className='text-sm font-normal text-slate-400'>
          Completa la evaluación estética y el estado de la batería para poder recibir el equipo.
        </span>
      </ModalHeader>
      <ModalBody className='bg-slate-50'>
        <div className='grid grid-cols-1 gap-4'>
          {showIAEvaluation
            ? (
              <>
                <IAEvaluation />
                {/* La batería recién se evalúa cuando la IA ya entregó su categoría. */}
                {showBattery && (useDiscounts ? <Discounts /> : <SwitchBattery />)}
              </>
              )
            : (
              <div className='flex flex-col gap-1 rounded-xl bg-amber-50 px-4 py-3 text-amber-700'>
                <div className='flex items-center gap-2'>
                  <MdError className='text-xl shrink-0' />
                  <span className='text-sm font-semibold'>Todavía no se puede realizar la evaluación estética:</span>
                </div>
                <ul className='list-disc pl-9 text-sm'>
                  {pendingRecalculate && <li>El equipo tiene un recálculo pendiente.</li>}
                  {pendingFeasibility && <li>El equipo tiene una revisión de factibilidad pendiente.</li>}
                  {!pendingRecalculate && !pendingFeasibility && <li>El equipo no cumple las condiciones para ser evaluado.</li>}
                </ul>
              </div>
              )}
        </div>
      </ModalBody>
      <ModalFooter>
        <AccionesTradein />
        <Button variant='light' onPress={onClose} isDisabled={loadingRecibir}>Cerrar</Button>
        {canRecibir && (
          <Button color='success' className='text-white' onPress={handleRecibir} isLoading={loadingRecibir} startContent={!loadingRecibir ? <MdCheckCircle className='text-base' /> : null}>
            {loadingRecibir ? 'Recibiendo…' : 'Recibir'}
          </Button>
        )}
      </ModalFooter>
    </>
  )
}

/* --------------------------------- Contenedor --------------------------------- */
// El contenido depende del tradein cargado, por eso vive dentro del provider.
const ModalContenido: FC<IContentProps & { isValidado: boolean }> = ({ isValidado, ...props }) => {
  const { ctx } = useTradeinContext()

  if (ctx.tradein.id === 0) {
    return (
      <ModalBody className='py-10'>
        <Spinner label='Cargando...' />
      </ModalBody>
    )
  }

  return isValidado ? <AestheticEvaluation {...props} /> : <TestPhonecheck {...props} />
}

const ModalTestPhonecheck: FC<IOwnProps> = ({ item, onDone }) => {
  const { isOpen, onOpen, onOpenChange } = useDisclosure()

  // El listado ya trae el estado, así se evita pedir el tradein solo para rotular el botón.
  const status = String(item.status).toUpperCase()
  const isValidado = item.id_status === STATUS_VALIDADO || status === 'VALIDADO'
  const hasBatteryStatus = item.isBatteryHealthy !== null && item.isBatteryHealthy !== undefined
  const label = !isValidado ? 'Realizar prueba' : hasBatteryStatus ? 'Recibir' : 'Evaluación Estética'
  // Desde Entregado el equipo ya se recibió: no queda prueba ni evaluación por hacer.
  const isFinalizado = ESTADOS_FINALIZADOS.includes(status)

  if (isFinalizado) return <span className='text-xs'>-</span>

  return (
    <>
      <Button size='sm' variant='flat' color='primary' startContent={isValidado ? <MdCheckCircle /> : <MdQrCode2 />} onPress={onOpen}>
        {label}
      </Button>
      {/* En la evaluación estética se cierra solo con los botones: los modales internos de batería
          y el resto de portales se renderizan fuera y un click ahí cerraría el modal. */}
      <Modal
        backdrop='blur'
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        size={isValidado ? '5xl' : 'md'}
        scrollBehavior='inside'
        placement='center'
        isDismissable={!isValidado}
        classNames={{ base: isValidado ? 'max-w-[95vw]' : 'max-w-[480px]' }}
      >
        <ModalContent>
          {(close) => (
            <TradeinProvider idProps={item.id}>
              <JoyRideProvider disabled>
                <ModalContenido
                  isValidado={isValidado}
                  loginCode={item.login_code}
                  onClose={close}
                  onDone={onDone}
                />
              </JoyRideProvider>
            </TradeinProvider>
          )}
        </ModalContent>
      </Modal>
    </>
  )
}

export default ModalTestPhonecheck
